import { useState } from 'react';
import { useI18n } from './i18n';
import { isOptionSymbol, pointValue } from './tools/contracts';
import FeeRuleForm from './FeeRules';

// Futures always have commissions; many stock brokers don't charge any, so stock trades
// without fees aren't flagged (options and stocks have no futures point value).
export function missingFees(trades) {
  return trades.filter((t) => !(t.fees > 0) && pointValue(t.symbol) !== undefined && !isOptionSymbol(t.symbol));
}

// A warning when some trades have no fees, listing the accounts they're in, each with a way
// to save that account's commission and fee rates (which also fills in future trades).
// `allTrades` are every trade (not just the period shown), so saving covers the whole account.
export default function MissingFeesNote({ trades, allTrades, accounts, onUpdated, onAccountSaved, onSessionExpired }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const missing = missingFees(trades);
  if (!missing.length) return null;

  const byAccount = new Map();
  for (const x of missing) byAccount.set(String(x.accountId), (byAccount.get(String(x.accountId)) || 0) + 1);
  const list = (accounts || []).filter((a) => byAccount.has(String(a._id)));
  const editingAccount = list.find((a) => a._id === editing);

  function saved({ account, trades: updated }) {
    if (account) onAccountSaved(account);
    if (updated && updated.length) onUpdated(updated);
    setEditing(null);
  }

  return (
    <div className="missing-fees" role="status">
      <p>
        <strong>{t(missing.length === 1 ? '{n} trade has no fees recorded.' : '{n} trades have no fees recorded.', { n: missing.length })}</strong>{' '}
        {t('Your net P&L and rules panel may look better than your real account until they’re added.')}
        {!open && list.length > 0 && <> <button type="button" className="link-button" onClick={() => setOpen(true)}>{t('Add fees')}</button></>}
      </p>
      {open && (
        <div className="missing-fees-accounts">
          <p className="field-hint">{t('Save each account’s commission and fees once. They’re added to these trades and to every new trade without fees in that account.')}</p>
          <ul>
            {list.map((a) => (
              <li key={a._id}>
                <div className="missing-fees-account">
                  <span><strong>{a.name}</strong> &middot; {t(byAccount.get(String(a._id)) === 1 ? '{n} trade without fees' : '{n} trades without fees', { n: byAccount.get(String(a._id)) })}</span>
                  {editing !== a._id && (
                    <button type="button" className="secondary small" onClick={() => setEditing(a._id)}>{a.feeRule ? t('Edit fees') : t('Set fees')}</button>
                  )}
                </div>
                {editingAccount && editing === a._id && (
                  <FeeRuleForm account={a} trades={(allTrades || trades).filter((x) => String(x.accountId) === String(a._id))}
                    onSaved={saved} onCancel={() => setEditing(null)} onSessionExpired={onSessionExpired} />
                )}
              </li>
            ))}
          </ul>
          {!editing && <button type="button" className="link-button" onClick={() => setOpen(false)}>{t('Close')}</button>}
        </div>
      )}
    </div>
  );
}
